import { useState } from "react";
import type { RestaurantFormData } from "../../restaurant.types";
import { Clock, Copy, XCircle, ChevronDown, Check } from "lucide-react";

interface OpeningHoursProps {
  formData: RestaurantFormData;
  updateFormData: (updatedFields: Partial<RestaurantFormData>) => void;
}

const days = [
  { key: "monday",    label: "Monday",    short: "Mon" },
  { key: "tuesday",   label: "Tuesday",   short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday",  label: "Thursday",  short: "Thu" },
  { key: "friday",    label: "Friday",    short: "Fri" },
  { key: "saturday",  label: "Saturday",  short: "Sat" },
  { key: "sunday",    label: "Sunday",    short: "Sun" },
] as const;

type DayKey = typeof days[number]["key"];

const PRESETS = [
  { label: "Standard (10:00 – 23:00)", open: "10:00", close: "23:00" },
  { label: "Breakfast (07:00 – 12:00)", open: "07:00", close: "12:00" },
  { label: "Lunch & Dinner (11:00 – 22:00)", open: "11:00", close: "22:00" },
  { label: "Late Night (18:00 – 02:00)", open: "18:00", close: "02:00" },
];

function parseHours(hours: string | undefined) {
  if (!hours) return { open: "10:00", close: "23:00", isClosed: false };
  if (hours.toLowerCase() === "closed") return { open: "", close: "", isClosed: true };

  const [openRaw, closeRaw] = hours.split(" - ").map((s) => s.trim());
  const to24 = (t: string) => {
    if (!t) return "";
    if (/^\d{2}:\d{2}$/.test(t)) return t;
    const d = new Date(`1970-01-01 ${t}`);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  return { open: to24(openRaw), close: to24(closeRaw), isClosed: false };
}

function formatDisplay(hours: string | undefined) {
  const { open, close, isClosed } = parseHours(hours);
  if (isClosed) return "Closed";
  if (!open || !close) return "—";
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${((h % 12) || 12)}:${String(m).padStart(2, "0")} ${ampm}`;
  };
  return `${fmt(open)} – ${fmt(close)}`;
}

/* ── Toggle switch ── */
const Toggle = ({
  checked,
  onChange,
  activeColor = "bg-emerald-500",
  inactiveColor = "bg-gray-200",
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  activeColor?: string;
  inactiveColor?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#FFD166]
      ${checked ? activeColor : inactiveColor}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
        ${checked ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

/* ── Time input ── */
const TimeInput = ({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) => (
  <div className="relative">
    <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
    <input
      type="time"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 rounded-lg bg-white
        focus:outline-none focus:ring-2 focus:ring-[#FFD166]/60 focus:border-[#FFD166]
        disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed
        transition-all appearance-none"
    />
  </div>
);

const OpeningHours: React.FC<OpeningHoursProps> = ({ formData, updateFormData }) => {
  const [showPresets, setShowPresets] = useState(false);
  const [copiedDay, setCopiedDay] = useState<DayKey | null>(null);
  const [copiedHours, setCopiedHours] = useState<{ open: string; close: string } | null>(null);

  const updateHours = (day: DayKey, open: string, close: string, isClosed: boolean) => {
    updateFormData({
      openingHours: {
        ...formData.openingHours,
        [day]: isClosed ? "Closed" : `${open} - ${close}`,
      },
    });
  };

  const applyPresetToAll = (open: string, close: string) => {
    const hours = Object.fromEntries(days.map((d) => [d.key, `${open} - ${close}`]));
    updateFormData({ openingHours: hours as unknown as RestaurantFormData["openingHours"] });
    setShowPresets(false);
  };

  const closeAll = () => {
    const closed = Object.fromEntries(days.map((d) => [d.key, "Closed"]));
    updateFormData({ openingHours: closed as unknown as RestaurantFormData["openingHours"] });
  };

  const copyDay = (day: DayKey) => {
    const { open, close, isClosed } = parseHours(formData.openingHours[day]);
    if (isClosed) return;
    setCopiedHours({ open, close });
    setCopiedDay(day);
  };

  const pasteToAll = () => {
    if (!copiedHours) return;
    const hours = Object.fromEntries(
      days.map((d) => [d.key, `${copiedHours.open} - ${copiedHours.close}`])
    );
    updateFormData({ openingHours: hours as unknown as RestaurantFormData["openingHours"] });
    setCopiedDay(null);
    setCopiedHours(null);
  };

  const openCount = days.filter((d) => {
    const h = formData.openingHours[d.key];
    return h && h.toLowerCase() !== "closed";
  }).length;

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
        .fade-in { animation: fadeIn 0.18s ease both; }
      `}</style>

      <div className="space-y-4">
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Opening Hours</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-semibold text-emerald-600">{openCount}</span> of {days.length} days open
            </p>
          </div>

          {/* Action toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            {copiedHours && (
              <button
                type="button"
                onClick={pasteToAll}
                className="fade-in flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FFD166] hover:bg-amber-400 text-gray-900 transition-colors shadow-sm"
              >
                <Check className="w-3.5 h-3.5" />
                Apply to all days
              </button>
            )}

            {/* Preset dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPresets((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:border-[#FFD166] hover:bg-amber-50 text-gray-700 transition-colors shadow-sm"
              >
                Set all hours
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPresets ? "rotate-180" : ""}`} />
              </button>

              {showPresets && (
                <div className="fade-in absolute right-0 top-full mt-1.5 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                  {PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => applyPresetToAll(p.open, p.close)}
                      className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-amber-50 hover:text-gray-900 transition-colors flex items-center justify-between gap-2"
                    >
                      <span>{p.label}</span>
                      <span className="text-gray-400 font-mono text-[10px]">{p.open}–{p.close}</span>
                    </button>
                  ))}
                  <div className="border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => { closeAll(); setShowPresets(false); }}
                      className="w-full text-left px-4 py-2.5 text-xs text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Close all days
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm bg-white">
          {/* Column headings */}
          <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
            <div className="col-span-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Day</span>
            </div>
            <div className="col-span-6">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hours (24h)</span>
            </div>
            <div className="col-span-3 text-right">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {days.map(({ key, label, short }, i) => {
              const { open, close, isClosed } = parseHours(formData.openingHours[key]);
              const isWeekend = key === "saturday" || key === "sunday";
              const isCopied = copiedDay === key;

              return (
                <div
                  key={key}
                  className={`grid grid-cols-12 gap-2 px-5 py-3 transition-colors
                    ${isClosed ? "items-center bg-gray-50/60" : "items-start sm:items-center hover:bg-amber-50/30"}
                    ${isWeekend && !isClosed ? "bg-amber-50/20" : ""}`}
                >
                  {/* Day label */}
                  <div className="col-span-3 flex items-center gap-2">
                    <div className={`hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-[10px] font-bold flex-shrink-0
                      ${isClosed ? "bg-gray-100 text-gray-400" : isWeekend ? "bg-amber-100 text-amber-700" : "bg-blue-50 text-blue-600"}`}>
                      {short.slice(0, 2)}
                    </div>
                    <div>
                      <span className={`text-sm font-semibold ${isClosed ? "text-gray-400" : "text-gray-800"}`}>
                        <span className="hidden sm:inline">{label}</span>
                        <span className="sm:hidden">{short}</span>
                      </span>
                      {!isClosed && (
                        <p className="text-[10px] text-gray-400 hidden md:block">{formatDisplay(formData.openingHours[key])}</p>
                      )}
                    </div>
                  </div>

                  {/* Time inputs */}
                  <div className="col-span-6">
                    {isClosed ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-500 border border-red-100 rounded-lg text-xs font-medium">
                        <XCircle className="w-3.5 h-3.5" />
                        Closed
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* Stacked on mobile, side-by-side on sm+ */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400 w-8 shrink-0 sm:hidden">Open</span>
                            <TimeInput
                              value={open}
                              disabled={isClosed}
                              onChange={(v) => updateHours(key, v, close, false)}
                            />
                          </div>
                          <span className="text-gray-400 text-xs shrink-0 select-none hidden sm:block">–</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400 w-8 shrink-0 sm:hidden">Close</span>
                            <TimeInput
                              value={close}
                              disabled={isClosed}
                              onChange={(v) => updateHours(key, open, v, false)}
                            />
                          </div>
                        </div>
                        {/* Copy-to-all shortcut */}
                        <button
                          type="button"
                          title="Copy these hours to all open days"
                          onClick={() => isCopied ? pasteToAll() : copyDay(key)}
                          className={`self-start sm:self-auto mt-0.5 sm:mt-0 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors
                            ${isCopied
                              ? "bg-[#FFD166] text-gray-900"
                              : "text-gray-300 hover:text-gray-500 hover:bg-gray-100"}`}
                        >
                          {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Open/Closed toggle */}
                  <div className="col-span-3 flex items-start sm:items-center justify-end gap-2 pt-0.5 sm:pt-0">
                    <span className={`text-xs font-medium hidden sm:block ${isClosed ? "text-red-400" : "text-emerald-600"}`}>
                      {isClosed ? "Closed" : "Open"}
                    </span>
                    <Toggle
                      checked={!isClosed}
                      onChange={(open) => updateHours(key, open ? "10:00" : "", open ? "23:00" : "", !open)}
                      activeColor="bg-emerald-500"
                      inactiveColor="bg-gray-200"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer summary */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              {days.map(({ key, short }) => {
                const { isClosed } = parseHours(formData.openingHours[key]);
                return (
                  <div key={key} className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${isClosed ? "bg-gray-300" : "bg-emerald-400"}`} />
                    <span className="text-[10px] text-gray-500">{short}</span>
                  </div>
                );
              })}
            </div>
            <span className="text-xs text-gray-400">
              {copiedHours
                ? `Ready to paste ${copiedHours.open}–${copiedHours.close} to all days`
                : "Click copy icon on any row to apply hours to all days"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default OpeningHours;